import { Form, IFormContext, arraySearch, IRowData, Modal, defaultMap, ISearchOptions } from 'rewire-ui';
import mainPageModel from '../main-navigation/MainPageModel';
import MasterDetailModel from '../settings/MasterDetailModel';
import EditDialogModel from '../schedule/EditDialogModel';
import gql from 'graphql-tag';
import { createTitle, createButton } from './CustomFormFields';
import { guardedMutation, guardedQuery } from '../graphql/GraphQLClient';
import { notifications } from '../ui/Notifications';
import DataQueries from '../graphql/Queries';
import { EntityType, INameEntity, DeletedScopeId } from '../DataTypes';
import uniqueId from 'lodash.uniqueid';
import { camelCase } from 'change-case';
import { pageState } from './DefaultSettingsView';
import { createColumn, createGrid, ICell, IColumn, IGrid } from 'rewire-grid';
import { match } from 'rewire-common';

export interface IExporterSettings {
  convertRatesToStraightTime: boolean;
  groupBy?: string[];
  totalBy?: string[];
  mergePremiums?: {
    premiumHoursEarned: INameEntity;
    workedHoursEarned: INameEntity;
  }[];
}

export interface IExporterFieldDefn {
  type: 'literal' | 'built_in' | 'custom' | 'empty';
  field: string;
  displayName: string;
  required?: boolean;
  fieldType: 'string' | 'number' | 'date';
  value?: string; // value if the type is literal;
  format?: string;
  sortPosition?: number;
  sortDirection?: 'asc' | 'desc';
}

export interface IExporterLayoutDefn {
  delimiter?: string; // default ','
  quote?: string; // default '"'
  headers?: string[][];
  fields: IExporterFieldDefn[];
}

export interface IExporterConfig {
  version: string;
  file: IExporterLayoutDefn;
  report: IExporterLayoutDefn;
  settings: IExporterSettings;
}

const _loadExportQuery = gql`
query {
  loadExportConfig {
    version
    file {
      delimiter
      quote
      headers
      fields {
        type
        field
        displayName
        required
        fieldType
        value
        format
        sortPosition
        sortDirection
      }
    }
    report {
      delimiter
      quote
      headers
      fields {
        type
        field
        displayName
        required
        fieldType
        value
        format
        sortPosition
        sortDirection
      }
    }
    settings {
      convertRatesToStraightTime
      groupBy
      totalBy
      mergePremiums {
        premiumHoursEarned {
          id
          code
          name
        }
        workedHoursEarned {
          id
          code
          name
        }
      }
    }
  }
}`;

const _saveExportQuery = gql`
mutation($exportConfig: ExporterConfigInput!) {
  saveExportConfig(exportConfig: $exportConfig)
}
`;

const _defaultTimecardSourceFields: IExporterFieldDefn[] = [
  {
    field: 'earnedDate',
    displayName: 'Earned Date',
    type: 'built_in',
    fieldType: 'date',
  },
  {
    field: 'employeeCode',
    displayName: 'Employee Code',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'employeeName',
    displayName: 'Employee Name',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'groupCode',
    displayName: 'Group Code',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'groupName',
    displayName: 'Group Name',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'occupationCode',
    displayName: 'Occupation Code',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'occupationName',
    displayName: 'Occupation Name',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'units',
    displayName: 'Units',
    type: 'built_in',
    fieldType: 'number',
  },
  {
    field: 'amount',
    displayName: 'Amount',
    type: 'built_in',
    fieldType: 'number',
  },
  {
    field: 'rate',
    displayName: 'Rate',
    type: 'built_in',
    fieldType: 'number',
  },
  {
    field: 'factor',
    displayName: 'Factor',
    type: 'built_in',
    fieldType: 'number',
  },
  {
    field: 'reasonCode',
    displayName: 'Reason Code',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'reasonName',
    displayName: 'Reason Name',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'overtimeCode',
    displayName: 'Overtime Code',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'overtimeName',
    displayName: 'Overtime Name',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'accountCode',
    displayName: 'Account Code',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'comment',
    displayName: 'Comment',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'category',
    displayName: 'Category',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'isReversal',
    displayName: 'Is Reversal',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'batchCode',
    displayName: 'Batch Code',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'batchName',
    displayName: 'Batch Name',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'batchStartField',
    displayName: 'Batch Start Field',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'batchEndField',
    displayName: 'Batch End Field',
    type: 'built_in',
    fieldType: 'string',
  },
  {
    field: 'customExpression',
    displayName: 'Custom Expression',
    type: 'custom',
    fieldType: 'string',
  },
];

const columnNumberFormatter = new Intl.NumberFormat('en-US', { minimumIntegerDigits: 2 });

async function loadExportConfig(): Promise<IExporterConfig> {
  const result = await guardedQuery(_loadExportQuery);
  return result?.data?.loadExportConfig as IExporterConfig;
}

async function saveExportConfig(exportConfig: IExporterConfig): Promise<boolean> {
  const result = await guardedMutation(_saveExportQuery, { exportConfig });
  return result?.data.saveExportConfig;
}

async function emptySave(_schema: string, _item: any[]) {
  return undefined;
}

async function emptyDelete(_ids: string[]) {
  return true;
}

async function emptyLoad(_ids: string[]) {
  return [];
}

type EditorModes = 'literal' | 'empty' | 'expression' | 'hidden';

const _types = ['string', 'date', 'number'];
interface IExportOutputFieldEditorModelState {
  mode: EditorModes;
}

export class ExportOutputFieldEditorModel extends EditDialogModel<IExportOutputFieldEditorModelState> {
  private model: TimecardExportConfigModel;
  constructor() {
    super(
      'Define Output Field',
      emptyLoad,
      emptySave,
      emptySave,
      emptyDelete,
      undefined,
      () => mainPageModel.canEditTimecardExporterRules,
      () => mainPageModel.canEditTimecardExporterRules,
      () => mainPageModel.canEditTimecardExporterRules,
    );

    this.state.mode = 'empty';
  }

  init(model: TimecardExportConfigModel) {
    this.model = model;
    this.form.field['sourceField'].onValueChange = () => this.sourceFieldUpdated();
    this.form.field['displayName'].onValueChange = () => this.displayNameUpdated();
  }

  get mode() {
    return this.state.mode;
  }

  set mode(mode: EditorModes) {
    if (this.state.mode === mode) return;
    this.state.mode = mode;
    this.updateVisibility(mode);
  }

  protected get name() {
    return this.form.field.displayName?.value ?? '';
  }

  _reset(defaultObj?: any) {
    super._reset(defaultObj);
    this.mode = 'hidden';
  }

  private updateVisibility(mode: EditorModes) {
    for (const field of this.form.fields) {
      if (field.name === 'addNewMapping') field.visible = mode === 'empty';
      else field.visible = mode === 'expression';
    }
  }

  updateEditor() {
    this.load();
    // this.mode = this.model.getMode();
  }

  async load(_id?: string, _isModal: boolean = true) {
    requestAnimationFrame(() => {
      this.isLoading = true;
      const selectedItem = this.model.selectedItem as IExporterFieldDefn;
      if (selectedItem) {
        this.form.value = this.mapToForm(selectedItem);
        this.mode = this.model.getMode();
        this.form.field['displayName'].visible = selectedItem.type === 'custom';
      } else {
        this.mode = 'hidden';
      }
      requestAnimationFrame(() => (this.isLoading = false));
    });
  }

  mapToForm(obj: ObjectType) {
    const v: IExporterFieldDefn & { sourceField: IExporterFieldDefn } = obj as any;
    v.sourceField =
      _defaultTimecardSourceFields?.find((f) => f.field === v.field) ??
      _defaultTimecardSourceFields[_defaultTimecardSourceFields.length - 1];
    return v;
  }

  sourceFieldUpdated() {
    if (this.isLoading) return;
    const sourceField = this.form.field['sourceField'].value as IExporterFieldDefn;
    const isCustomField = sourceField?.type === 'custom' && this.mode === 'expression';
    const f = this.form.field['displayName'];
    f.value = undefined;
    if (isCustomField) {
      f.visible = true;
    } else {
      f.visible = false;
    }
    this.model.updateSelectedItem({
      field: sourceField.field,
      fieldType: sourceField.fieldType,
      type: sourceField.type,
      displayName: isCustomField ? f.value : sourceField.displayName,
    });
  }

  displayNameUpdated() {
    if (this.isLoading) return;
    const displayName = this.form.field['displayName'].value;
    this.model.updateSelectedItem({ displayName });
  }

  async remove() {
    const result = await super.remove();
    return result;
  }

  protected _createForm(): Form {
    return Form.create((form: IFormContext) => ({
      sourceHeader: createTitle(form, 'Timecard Source', 'unindent'),
      addNewMapping: createButton(form, 'Map Timecard Field', () => this.model.addExpression()),
      sourceField: form
        .select(arraySearch(_defaultTimecardSourceFields, (item: IExporterFieldDefn) => item?.displayName ?? '«empty»'))
        .validators('required')
        .label('Source Field')
        .placeholder('enter source field'),
      displayName: form
        .string()
        .label('Custom Expression Name')
        .updateOnChange(true)
        .placeholder('enter the custom expression field name'),
      outputHeader: createTitle(form, 'Output', 'unindent'),
      required: form.boolean().label('Is Required?').updateOnChange(true).placeholder('is field required?').validators('required'),
      fieldType: form
        .select(arraySearch(_types, (item: any) => item ?? ''))
        .validators('required')
        .label('Type')
        .updateOnChange(true)
        .placeholder('enter type'),
      format: form.string().label('Format').updateOnChange(true).placeholder('enter format'),
      sortPosition: form.number().label('Sort Position').updateOnChange(true).placeholder('enter a number > 0'),
      sortDirection: form
        .select(arraySearch(['asc', 'desc'], (item: any) => item ?? ''))
        .label('Sort Direction')
        .updateOnChange(true)
        .disabled(() => !(this.form.field['sortPosition'].value > 0))
        .placeholder('enter the sort direction'),
    }));
  }
}

async function importDataFromExcel(cell?: ICell) {
  if (!cell) return;
  const grid = cell.grid;
  const r = await navigator.clipboard.readText();
  if (!r) return;
  const rows = r.split('\n');
  let startRow: ICell | undefined = cell;
  for (const row of rows) {
    const data = row.split('\t');
    let startCol: ICell | undefined = startRow;
    for (const value of data) {
      if (startCol) {
        startCol.value = value;
        startCol = grid.adjacentRightCell(startCol);
      }
    }
    if (startRow) startRow = grid.adjacentBottomCell(startRow);
    else break;
  }
}

export class TimecardExportConfigModel extends MasterDetailModel {
  exportLayout: IExporterLayoutDefn;
  headerDefinition: IGrid;
  headerForm = Form.create((form: IFormContext) => ({
    headerRows: form
      .number()
      .label('# of Rows')
      .onValueChange(() => requestAnimationFrame(() => this.recreateGrid()))
      .placeholder('enter a number > 0'),
    headerColumns: form
      .number()
      .label('# of Columns')
      .onValueChange(() => requestAnimationFrame(() => this.recreateGrid()))
      .placeholder('enter a number > 0'),
    quote: form.string().label('Quote (Blank for None)'),
    delimiter: form.string().label('Delimiter (Blank for Comma Separated)'),
  }));

  public constructor() {
    super(new ExportOutputFieldEditorModel());
    (this.editor as ExportOutputFieldEditorModel).init(this);

    const gridVariableKeybinds = {
      'Ctrl+Insert': (evt: React.KeyboardEvent<any>, cell: ICell) => {
        this.insertColumn(cell);
        evt.stopPropagation();
        evt.preventDefault();
      },
      'Ctrl+Delete': (evt: React.KeyboardEvent<any>, cell: ICell) => {
        this.deleteColumn(cell);
        evt.stopPropagation();
        evt.preventDefault();
      },
      Delete: (evt: React.KeyboardEvent<any>, cell: ICell) => {
        if (cell.editing) return;
        this.deleteCell(cell);
        evt.stopPropagation();
        evt.preventDefault();
      },
      'Ctrl+D': (evt: React.KeyboardEvent<any>, _cell: ICell) => {
        evt.stopPropagation();
        evt.preventDefault();
      },
      'Ctrl+X': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {
        // remove cut functionality
      },
      'Ctrl+V': async (evt: React.KeyboardEvent<any>, cell: ICell | undefined) => {
        await importDataFromExcel(cell);
        evt.stopPropagation();
        evt.preventDefault();
      },
    };

    this.headerDefinition = createGrid([], [], { variableKeybinds: gridVariableKeybinds });
    this.headerForm.value = { headerRows: 1, headerColumns: 8 };
    this.recreateGrid();
  }

  deleteCell(cell?: ICell) {
    if (!cell) return;
    cell.value = undefined;
    this.updateSelectedItem(this.createEmptyField());
  }

  deleteColumn(cell?: ICell) {
    if (!cell) return;
    const columnPosition = cell.columnPosition;
    this.headerDefinition.spliceColumns(columnPosition, 1);
    this.items.splice(columnPosition, 1);

    for (let colIdx = 0; colIdx < this.headerDefinition.columns.length; colIdx++) {
      const column = this.headerDefinition.columns[colIdx];
      column.position = colIdx;
      this.headerDefinition.fixedRows[0].cells[column.name].value = columnNumberFormatter.format(colIdx + 1);
    }

    this.headerForm.field.headerColumns.value -= 1;
    this.select(undefined);
  }

  insertColumn(cell?: ICell) {
    if (!cell) return;
    const columnPosition = cell.columnPosition;
    const column = createColumn(uniqueId('col'), '', { width: '180px', onValueChange: this.onCellValueChanged, canSort: false });

    this.selectItem(undefined);

    this.headerDefinition.spliceColumns(columnPosition, 0, column);
    // for some reason rewire does not set the position correctly on the column
    for (let colIdx = 0; colIdx < this.headerDefinition.columns.length; colIdx++) {
      const column = this.headerDefinition.columns[colIdx];
      column.position = colIdx;
      this.headerDefinition.fixedRows[0].cells[column.name].value = columnNumberFormatter.format(colIdx + 1);
    }

    for (let rowIdx = 0; rowIdx < this.headerDefinition.rows.length; rowIdx++) {
      const row = this.headerDefinition.rows[rowIdx];
      const newCell = row.createCell(column, undefined);
      if (rowIdx === this.headerDefinition.rows.length - 1) newCell.cls = 'detail';
    }

    this.items.splice(columnPosition, 0, { type: 'empty' });
    this.headerForm.field.headerColumns.value += 1;
  }

  addExpression = () => {
    if (!this._currentCell) return;
    this.updateSelectedItem({ field: 'customExpression', displayName: undefined, type: 'custom', fieldType: 'string' });
  };

  select(newItem: unknown | undefined) {
    this.state.selectedItem = newItem;
    requestAnimationFrame(() => {
      this.selectItem(newItem);
    });
  }

  private isLiteral(cell?: ICell) {
    if (!cell) return false;
    const currentValue = cell.value;
    return currentValue && !currentValue?.includes('«');
  }

  private setupCells() {
    const detailRow = this.headerDefinition.rows[this.headerDefinition.rows.length - 1];
    if (!detailRow) return;
    for (const cell of Object.values(detailRow.cells)) {
      const item: IExporterFieldDefn = this.items[cell.columnPosition];
      switch (item.type) {
        case 'literal':
          cell.value = item.value;
          break;
        case 'empty':
          cell.value = undefined;
          break;
        case 'custom':
        case 'built_in':
          cell.value = `«${item.displayName}»`;
          break;
      }
    }

    const headers = this.exportLayout.headers;
    if (!headers) return;
    for (let rowIdx = 0; rowIdx < headers.length; rowIdx++) {
      const row = headers[rowIdx];
      if (!row) continue;
      for (let colIdx = 0; colIdx < row.length; colIdx++) {
        const col = row[colIdx];
        const cell = this.headerDefinition.cellByPos(rowIdx, colIdx);
        if (cell) cell.value = col;
      }
    }
  }

  updateGrid() {
    if (!this._currentCell || !this.selectedItem) return;
    if (this.selectedItem.type === 'empty') {
      if (this._currentCell?.value) this._currentCell.value = undefined;
      return;
    }
    if (this.selectedItem.type === 'literal') {
      return;
    }

    this._currentCell.value = `«${this.selectedItem.displayName}»`;
  }

  getMode(): EditorModes {
    const type = this._currentCell ? (this.items[this._currentCell.columnPosition] as IExporterFieldDefn)?.type : undefined;
    if (!type) return 'hidden';
    switch (type) {
      case 'literal':
        return 'literal';
      case 'empty':
        return 'empty';
      case 'custom':
      case 'built_in':
        return 'expression';
    }
  }

  _currentCell: ICell | undefined;
  selectCell = async () => {
    const selectedCells = this.headerDefinition.selectedCells;
    const cell = selectedCells?.[0];
    if (!cell) return;
    if (cell === this._currentCell || cell.id === this._currentCell?.id) return;

    if (this._currentCell) {
      const value = this.editor?.form.toObjectValues();
      if (value) delete value.sourceField;
      this.updateSelectedItem(value);
      this._currentCell.cls = 'detail';
    }

    // not a detail row?
    if (!cell.cls?.includes('detail')) {
      this._currentCell = undefined;
      this.select(undefined);
    } else {
      // select detail row if not selected
      this._currentCell = cell;
      this._currentCell.cls = 'detail selected';
      this.select(this.items[cell.columnPosition]);
      requestAnimationFrame(() => this.updateGrid());
    }
  };

  updateSelectedItem(newItem?: Partial<IExporterFieldDefn>) {
    if (!newItem || !this._currentCell) return;
    const item: IExporterFieldDefn = this.items[this._currentCell.columnPosition];
    if (!item) return;
    Object.assign(item, newItem);
    if (newItem.displayName && item.type === 'custom') {
      item.field = camelCase(`custom${item.displayName}`);
    }
    if (newItem.value && item.type === 'literal') {
      item.field = camelCase(`custom${item.value}`);
    }
    requestAnimationFrame(() => {
      this.updateGrid();
      (this.editor as ExportOutputFieldEditorModel)!.updateEditor();
    });
  }

  onCellValueChanged = (cell?: ICell) => {
    if (!cell) return;
    if (!this.isLiteral(cell) || !cell.value) return;
    this.updateSelectedItem({ type: 'literal', value: cell.value });
  };

  protected recreateGrid() {
    const headerRows = this.headerForm.field.headerRows.value;
    const headerColumns = this.headerForm.field.headerColumns.value;
    if (typeof headerColumns === 'number' && headerColumns !== this.headerDefinition.columns.length) {
      if (headerColumns > this.headerDefinition.columns.length) {
        const columns: IColumn[] = [];
        for (let column = this.headerDefinition.columns.length; column < headerColumns; column++) {
          const c = createColumn(uniqueId('col'), columnNumberFormatter.format(column + 1), {
            width: '180px',
            onValueChange: this.onCellValueChanged,
            canSort: false,
          });
          c.position = column;
          columns.push(c);
        }
        this.headerDefinition.spliceColumns(this.headerDefinition.columns.length, 0, ...columns);
      } else {
        this.headerDefinition.spliceColumns(headerColumns, this.headerDefinition.columns.length - headerColumns);
      }
    }

    this.items.set(this.headerDefinition.columns.map((c, i) => this.items[i] ?? { type: 'empty' }));

    const numberOfExistingHeaderRows = this.headerDefinition.rows.length - 1;
    let detailRow: IRowData = this.headerDefinition.rows[numberOfExistingHeaderRows];
    if (!detailRow) detailRow = { data: {}, id: 'detailRow' };
    const rows: IRowData[] = [];
    for (let row = 0; row < headerRows; row++) {
      if (row < numberOfExistingHeaderRows) rows.push(this.headerDefinition.rows[row]);
      else rows.push({ data: {}, id: `row${row}` });
    }
    rows.push(detailRow);
    this.headerDefinition.set(rows);

    const r = this.headerDefinition.row('detailRow');
    if (!r?.cells) return;
    for (const cell of Object.values(r.cells)) {
      cell.cls = 'detail';
    }
  }

  get canAdd() {
    return this.editor!.canAdd && this.canAddItem();
  }

  get canDelete() {
    return this.selectedItem && ((this.canAdd && this.editor!.isNew) || (this.editor!.canDelete && !this.editor!.isNew));
  }

  protected _handleOnSubmit() {
    super._handleOnSubmit();
  }

  createEmptyField(): IExporterFieldDefn {
    return { type: 'empty', value: undefined, displayName: '', field: '', fieldType: 'string' };
  }

  set(exportLayout: IExporterLayoutDefn): void {
    this.exportLayout = exportLayout;
    this.editor!.reset();
    if (this._currentCell) {
      this._currentCell.cls = 'detail';
      this._currentCell.value = undefined;
      this._currentCell = undefined;
    }
    const headerRows = exportLayout.headers?.length ?? 1;
    let headerColumns = exportLayout.headers?.[0]?.length ?? exportLayout.fields.length ?? 6;
    if (headerColumns === 0) headerColumns = 6;
    const exportFields = exportLayout.fields;
    this.items.length = headerColumns;
    for (let idx = 0; idx < this.items.length; idx++) {
      this.items[idx] = exportFields[idx] ?? this.createEmptyField(); // deep clone objects
    }
    this.headerForm.value = { headerRows, headerColumns, quote: exportLayout.quote, delimiter: exportLayout.delimiter };
    this.recreateGrid();
    this.setupCells();
    this.select(undefined);
  }

  get(): IExporterLayoutDefn {
    const headers = [];
    for (let rowIdx = 0; rowIdx < this.headerDefinition.rows.length - 1; rowIdx++) {
      const row = this.headerDefinition.rows[rowIdx];
      const cols: string[] = [];
      for (let colIdx = 0; colIdx < this.headerDefinition.columns.length; colIdx++) {
        const column = this.headerDefinition.columns[colIdx];
        cols.push(row.cells[column.name].value);
      }
      headers.push(cols);
    }

    if (this.editor?.form.hasChanges && !this.editor?.form.hasErrors) {
      const formChanges = this.editor?.form.toObjectValues();
      const item = {
        fieldType: formChanges.fieldType,
        format: formChanges.format,
        required: formChanges.required,
        sortPosition: formChanges.sortPosition,
        sortDirection: formChanges.sortDirection,
      };
      this.updateSelectedItem(item);
    }

    const quote = this.headerForm.field.quote.value;
    const delimiter = this.headerForm.field.delimiter.value;

    return {
      fields: this.items.map((i) => ({
        type: i.type,
        field: i.field,
        displayName: i.displayName,
        required: i.required,
        fieldType: i.fieldType,
        value: i.value,
        format: i.format,
        sortPosition: i.sortPosition,
        sortDirection: i.sortDirection,
      })),
      headers,
      quote,
      delimiter,
    };
  }
}

interface ITimecardExportConfigModelState {
  visible: boolean;
}

const defaultNameSearchReturnSchema = `
  id
  name
  code
  display
`;

export function arraySearch2<T>(suggestionsFn: () => T[], map?: (item?: T) => string, _value?: T) {
  const _map = map || defaultMap;

  function getSuggestions(value: string): T[] {
    let count = 0;
    const regex = match(value, 'i');
    const suggestions = suggestionsFn();
    return value.length === 0
      ? suggestions
      : suggestions.filter((suggestion) => {
          const keep = count < 8 && regex.test(_map(suggestion as any));

          if (keep) {
            count += 1;
          }
          return keep;
        });
  }

  function search(searchText: string, _options?: ISearchOptions): Promise<T[]> {
    return Promise.resolve(getSuggestions(searchText));
  }

  return { map: _map, search };
}

// const getArrayStringMap = (map?: (value: any) => any) => (value: any) => {
//   if (!value) return '';
//   if (!Array.isArray(value)) value = [value];

//   const values = (map) ? value.map((v: any) => map(v)) : value;
//   return values.join(', ');
// };

// const defaultMap     = (value: any) => (value?.display) || (value?.name) || value || '';
// const getArrayString = getArrayStringMap(defaultMap);

function createHoursEarnedSearch() {
  const filter = { field: 'type', eq: 'hoursEarnedType' };
  const type = 'documents';
  return {
    async search(search?: string) {
      const r = await DataQueries.getSearch(defaultNameSearchReturnSchema, type as EntityType, search, filter);
      if (Array.isArray(r)) r.unshift({ id: DeletedScopeId, name: '«empty»', display: '«empty»' });
      return r;
    },
    map(item: any) {
      if (Array.isArray(item)) return '';
      return item?.name;
    },
  };
}

export const hoursEarnedSearcher = createHoursEarnedSearch();

const getGroupByItems = () =>
  _defaultTimecardSourceFields
    .filter((f) => f.type === 'built_in')
    .concat(timecardExportConfigDialogModel.fileExportConfigModel.items.filter((f) => f.type === 'custom'));
const getTotalByItems = () =>
  timecardExportConfigDialogModel.fileExportConfigModel.items.filter((v) => v.type === 'custom' && v.fieldType === 'number');

export class TimecardExportSettingsModel {
  get(): IExporterSettings {
    const v = JSON.parse(JSON.stringify(this.rollupForm.toObject() ?? {})); // clone to remove proxies
    const mergePremiums =
      v.mergePremiumHours && v.mergeWorkedHours
        ? [
            {
              premiumHoursEarned: v.mergePremiumHours,
              workedHoursEarned: v.mergeWorkedHours,
            },
          ]
        : [];

    const groupByItems = getGroupByItems();
    const totalByItems = getTotalByItems();
    const result: IExporterSettings = {
      mergePremiums,
      convertRatesToStraightTime: v?.convertRatesToStraightTime ?? false,
      groupBy:
        v.groupBy
          ?.filter((gb: IExporterFieldDefn) => groupByItems.some((f) => f.field === gb?.field))
          .map((gb: IExporterFieldDefn) => gb?.field) ?? [],
      totalBy:
        v.totalBy
          ?.filter((tb: IExporterFieldDefn) => totalByItems.some((f) => f.field === tb?.field))
          .map((tb: IExporterFieldDefn) => tb?.field) ?? [],
    };
    return result;
  }

  set(settings: IExporterSettings) {
    const groupByItems = getGroupByItems();
    const totalByItems = getTotalByItems();
    const v = {
      convertRatesToStraightTime: settings.convertRatesToStraightTime,
      mergePremiumHours: settings.mergePremiums?.[0]?.premiumHoursEarned,
      mergeWorkedHours: settings.mergePremiums?.[0]?.workedHoursEarned,
      groupBy: settings.groupBy?.map((gb) => groupByItems.find((f) => f.field === gb)),
      totalBy: settings.totalBy?.map((tb) => totalByItems.find((f) => f.field === tb)),
    };
    this.rollupForm.value = v;
  }

  rollupForm = Form.create((form: IFormContext) => ({
    outputHeader: createTitle(form, 'Convert Rates', 'unindent'),
    convertRatesToStraightTime: form.boolean().label('Convert Rate to Straight Time? Rate = (Rate * Factor), Factor = 1'),
    mergePremiums: createTitle(form, 'Merge Premiums', 'unindent'),
    mergePremiumHours: form.select(hoursEarnedSearcher).editor('auto-complete').label('Merge these Timecard Premiums'),
    mergeWorkedHours: form.select(hoursEarnedSearcher).editor('auto-complete').label('Into these Worked Timecards'),
    rollupHeader: createTitle(form, 'Rollup Timecards By Grouping On', 'unindent'),
    groupBy: form
      .multiselectautocomplete(
        arraySearch2(getGroupByItems, (item: IExporterFieldDefn) => item?.displayName),
        { chipLimit: 6 },
      )
      .label('Group By'),
    totalBy: form
      .multiselect(
        arraySearch2(getTotalByItems, (item: IExporterFieldDefn) => item?.displayName),
        { chipLimit: 6 },
      )
      .label('Additional Grouped Totals'),
  }));
}

export class TimecardExportConfigDialogModel extends Modal<ITimecardExportConfigModelState> {
  public exportConfig: IExporterConfig;
  fileExportConfigModel: TimecardExportConfigModel;
  reportExportConfigModel: TimecardExportConfigModel;
  settingsExportConfigModel: TimecardExportSettingsModel;
  constructor() {
    super('Define Output File Layout', false);
    this.fileExportConfigModel = new TimecardExportConfigModel();
    this.reportExportConfigModel = new TimecardExportConfigModel();
    this.settingsExportConfigModel = new TimecardExportSettingsModel();
    this.action('save', this.submit, {
      icon: 'save',
      variant: 'contained',
      color: 'primary',
      disabled: () => false,
      type: 'submit',
    });
    this.action('close');
    this.state.visible = true;
  }

  get visible() {
    return this.state.visible;
  }

  async open(): Promise<void> {
    await this.load();
    this.fileExportConfigModel.editor!.visible = true;
    this.reportExportConfigModel.editor!.visible = true;
    super.open();
  }

  close = () => {
    this.state.open = false;
    this.fileExportConfigModel.editor!.visible = false;
    this.reportExportConfigModel.editor!.visible = false;
  };

  async load(): Promise<void> {
    this.exportConfig = await loadExportConfig();
    // this.exportConfig = undefined; // comment out when editor is complete
    if (!this.exportConfig) {
      this.exportConfig = { file: { fields: [] }, report: { fields: [] } } as unknown as IExporterConfig;
    } else if (!this.exportConfig?.version) {
      this.state.visible = false;
      notifications.error('you are using a built-in exporter which cannot be edited');
      return;
    }

    // clone exporter config so that you don't make any edits to the original and cancel will work!!
    const config = JSON.parse(JSON.stringify(this.exportConfig)) as IExporterConfig;
    this.fileExportConfigModel.set(config.file);
    this.reportExportConfigModel.set(config.report);
    this.settingsExportConfigModel.set(config.settings ?? {});
  }

  async submit() {
    const exportConfig: IExporterConfig = {
      version: '1.0.0',
      file: this.fileExportConfigModel.get(),
      report: this.reportExportConfigModel.get(),
      settings: this.settingsExportConfigModel.get(),
    };
    try {
      const result = await saveExportConfig(exportConfig);
      if (!result) return false;
      notifications.success('export saved successfully!');
      const settings = pageState.__settings;
      if (settings?.selectedItem?.name === 'Exporter Mapping Rules') {
        const id = settings.selectedItem?.id;
        settings.select(undefined);
        settings.select(id);
      }
    } catch (err) {
      notifications.error(err);
      return false;
    }
    this.exportConfig = exportConfig;
    return false;
  }
}

const timecardExportConfigDialogModel = new TimecardExportConfigDialogModel();
export default timecardExportConfigDialogModel;
